<template lang="pug">
div(style='display: flex; align-items: center; height: 100%; padding: 0 12px; gap: 0; font-size: 0.72rem; background: #111827; border-top: 1px solid rgba(255,255,255,0.08); overflow: hidden; user-select: none;')
    //- STANAG-4857 — the operator-facing badge for the CATS/STANAG
    //- track stream.  Data is sourced from the backend's `chorus.mqtt`
    //- status field (the CEOM broker is what carries the STANAG feed
    //- on REPMUS 26), so the plumbing is MQTT under the hood, but the
    //- badge reflects what the operator recognises.  The separate
    //- generic "MQTT" indicator was removed as redundant.
    //-
    //- Gated behind VITE_STANAG_STATUS_ENABLED so environments without
    //- a STANAG feed don't render an idle badge.
    template(v-if='stanagStatusEnabled')
        div(style='display: flex; align-items: center; gap: 7px; flex-shrink: 0;')
            span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.4); text-transform: uppercase;') STANAG-4857
            div(:style='connectionDotStyle(stanagRunning, stanagConnected)' style='width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;')
            span(style='color: rgba(255,255,255,0.45); max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;') {{ stanagLabel }}
            svg(width='72' height='20' :viewBox='`0 0 72 20`' style='overflow: visible; flex-shrink: 0;')
                polyline(
                    v-if='stanagPoints'
                    :points='stanagPoints'
                    fill='none'
                    :stroke='stanagConnected ? "#a78bfa" : "rgba(255,255,255,0.2)"'
                    stroke-width='1.5'
                    stroke-linejoin='round'
                    stroke-linecap='round'
                )
            span(style='min-width: 72px; color: rgba(255,255,255,0.6);') {{ formatRate(latestStanagRate) }}
        div(style='width: 1px; height: 14px; background: rgba(255,255,255,0.12); flex-shrink: 0; margin: 0 12px;')
    //- WIRE — workstation puller → sidecar TCP stream.  Same badge
    //- shape as STANAG-4857 and AIS.  Data is a post-dedup subset of
    //- mqtt_messages rows tagged by the sidecar (SourceFile starts
    //- with wire://), so it counts what the puller-wire path
    //- successfully committed — not what LEDBAT admits or the wire
    //- drops.  Zero when no puller is connected OR the sidecar
    //- hasn't been redeployed with the tagging patch.
    template(v-if='stanagStatusEnabled')
        div(style='display: flex; align-items: center; gap: 7px; flex-shrink: 0;')
            span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.4); text-transform: uppercase;') WIRE
            div(:style='connectionDotStyle(true, wireConnected)' style='width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;')
            svg(width='72' height='20' :viewBox='`0 0 72 20`' style='overflow: visible; flex-shrink: 0;')
                polyline(
                    v-if='wirePoints'
                    :points='wirePoints'
                    fill='none'
                    :stroke='wireConnected ? "#fbbf24" : "rgba(255,255,255,0.2)"'
                    stroke-width='1.5'
                    stroke-linejoin='round'
                    stroke-linecap='round'
                )
            span(style='min-width: 72px; color: rgba(255,255,255,0.6);') {{ formatRate(latestWireRate) }}
        div(style='width: 1px; height: 14px; background: rgba(255,255,255,0.12); flex-shrink: 0; margin: 0 12px;')
    //- AIS — three-way source ladder.  Prefer the dedicated TCP
    //- AIS listener when it's configured (older deployments with
    //- MarineTraffic-style feeds).  Fall back to AIS-over-STANAG
    //- when the CEOM bridge is publishing (REPMUS 26).  When
    //- neither is present, badge reads "Not configured".  The
    //- "silent since Nm" annotation appears when the AIS-bridge
    //- rate is 0 but we've seen bridge traffic at least once
    //- this session — that's the case the diagnosis on 2026-09-06
    //- caught only after the operator noticed vessels missing
    //- from the map.
    div(style='display: flex; align-items: center; gap: 7px; flex-shrink: 0;')
        span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.4); text-transform: uppercase;') AIS
        div(:style='connectionDotStyle(aisRunning, aisConnected)' style='width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;')
        span(style='color: rgba(255,255,255,0.45); max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;' :title='aisTooltip') {{ aisLabel }}
        svg(width='72' height='20' :viewBox='`0 0 72 20`' style='overflow: visible; flex-shrink: 0;')
            polyline(
                v-if='aisPoints'
                :points='aisPoints'
                fill='none'
                :stroke='aisConnected ? "#60a5fa" : "rgba(255,255,255,0.2)"'
                stroke-width='1.5'
                stroke-linejoin='round'
                stroke-linecap='round'
            )
        span(style='min-width: 72px; color: rgba(255,255,255,0.6);') {{ formatRate(latestAisRate) }}
        span(
            v-if='aisSilentSuffix'
            style='color: rgba(255,180,120,0.9); font-size: 0.65rem; margin-left: 4px;'
            :title='aisTooltip'
        ) {{ aisSilentSuffix }}
    div(style='width: 1px; height: 14px; background: rgba(255,255,255,0.12); flex-shrink: 0; margin: 0 12px;')
    //- Map-cursor coordinate — populated 300 ms after the operator's
    //- mouse settles on the tactical chart (see TheCatlMap.vue
    //- onMapMouseMove).  Hides during motion so we're not repainting
    //- the DMM every frame while the cursor pans.
    div(
        v-if='mapCursorDmm'
        style='display: flex; align-items: center; gap: 7px; flex-shrink: 0;'
        :title='mapCursorDmm'
    )
        span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.4); text-transform: uppercase;') Cursor
        span(style='color: rgba(255,255,255,0.65); font-family: monospace; font-size: 0.72rem;') {{ mapCursorDmm }}
    div(v-if='mapCursorDmm' style='width: 1px; height: 14px; background: rgba(255,255,255,0.12); flex-shrink: 0; margin: 0 12px;')
    //- Backend health — appears only when a recent transient
    //- 4xx/5xx/network error is on record.  Replaces the toast-popup
    //- flow for temporary backend hiccups so operators aren't
    //- disrupted by a self-healing 500.  The badge auto-dismisses
    //- once nothing else has failed for two minutes.
    BackendHealthBadge
    //- CPA/TCPA alerts — polls the backend evaluator every 5 s and pops
    //- a panel on click for a summary of recent firings.
    AlertsStatusBadge
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import AlertsStatusBadge from '@/alerts/AlertsStatusBadge.vue';
import BackendHealthBadge from '@/common/BackendHealthBadge.vue';
import { useMapCursorStore } from '@/store/storeMapCursor';
import { formatDmm } from '@/util/latLonDmm';

const mapCursorStore = useMapCursorStore();
const mapCursorDmm = computed<string | null>(() => {
    const c = mapCursorStore.coord;
    if (!c) return null;
    return formatDmm(c.lat, c.lon);
});

const MQTT_STATUS_QUERY = gql`
    query StatusBarMqtt {
        chorus {
            mqtt {
                brokerUrl
                isRunning
                isConnected
                messagesPerMinute
                aisBridgeMessagesPerMinute
                aisBridgeDedupedRowsPerMinute
                aisBridgeLastMessageAt
                wireMessagesPerMinute
            }
        }
    }
`;

const AIS_STATUS_QUERY = gql`
    query StatusBarAis {
        chorus {
            ais {
                tcpUrl
                isRunning
                isConnected
                messagesPerMinute
            }
        }
    }
`;

const HISTORY = 40;
const SVG_W = 72;
const SVG_H = 20;
// Status-metadata (isRunning / isConnected / messagesPerMinute) doesn't
// change often — a broker disconnect worth knowing about within 20 s,
// not 3 s. Polling every 3 s meant this component fired 20+ requests
// per minute per user and competed with LCP-critical fetches on page
// load. Track *data* (AIS positions) is already SSE via
// `onTrackUpdate` — that path is unaffected by this change.
const POLL = 20000;

// Feature flag — resolved once at module load (Vite inlines it at
// build time).  Hides the STANAG badge AND skips the poll query when
// disabled, so backends that don't expose `chorus.mqtt` (dev boxes,
// tutorials) don't get 403s.  Data still comes from `chorus.mqtt`
// because the CEOM broker is what carries the STANAG feed on
// REPMUS 26 — if the backend ever splits STANAG onto a dedicated
// resolver, this useQuery flips over without touching the template.
const stanagStatusEnabled = import.meta.env.VITE_STANAG_STATUS_ENABLED === 'true';

const { result: mqttResult } = useQuery(
    MQTT_STATUS_QUERY,
    {},
    { pollInterval: POLL, fetchPolicy: 'network-only', enabled: stanagStatusEnabled },
);
const { result: aisResult } = useQuery(AIS_STATUS_QUERY, {}, { pollInterval: POLL, fetchPolicy: 'network-only' });

const mqttStatus = computed(() => mqttResult.value?.chorus?.mqtt ?? null);
const aisStatus = computed(() => aisResult.value?.chorus?.ais ?? null);

const stanagConnected = computed(() => mqttStatus.value?.isConnected === true);
const stanagRunning = computed(() => mqttStatus.value?.isRunning === true);

// AIS status has two possible sources:
//   1. chorus.ais — the dedicated TCP AIS listener (MarineTraffic).
//      A tcpUrl gets baked in from env config on dev boxes (default
//      "localhost:10110") even when nothing is actually listening
//      there, so URL-alone can't gate the badge — we need evidence
//      the listener is actually connected.
//   2. chorus.mqtt.aisBridge* — subset of the STANAG feed that
//      carries ais_mmsi / ais_ship_name external identifiers
//      (CEOM's cmre.ais_bridge).  This is what REPMUS 26 uses.
// The bridge takes over whenever the native listener isn't
// currently connected — that covers "URL configured but nothing
// listening" (REPMUS 26 today) AND "URL blank" (fresh deploy).
const nativeAisLive = computed(() => aisStatus.value?.isConnected === true);
const aisBridgeSeen = computed(() => mqttStatus.value?.aisBridgeLastMessageAt != null);
const usingAisBridge = computed(() => !nativeAisLive.value && aisBridgeSeen.value);

const aisConnected = computed(() => {
    if (usingAisBridge.value) return stanagConnected.value;   // AIS-over-STANAG piggybacks on MQTT health
    return aisStatus.value?.isConnected === true;
});
const aisRunning = computed(() => {
    if (usingAisBridge.value) return stanagRunning.value;
    return aisStatus.value?.isRunning === true;
});

const stanagLabel = computed(() => {
    if (!mqttStatus.value?.brokerUrl) return 'Not configured';
    return mqttStatus.value.brokerUrl.replace(/^mqtt[s]?:\/\//, '');
});

const aisLabel = computed(() => {
    if (usingAisBridge.value) return 'via STANAG';
    if (nativeAisLive.value) return aisStatus.value!.tcpUrl.replace(/^tcp:\/\//, '');
    // Neither is publishing.  Prefer to show the bridge label so
    // "silent Nm" hints in the AIS-over-STANAG case work; only fall
    // back to the (possibly stale) native URL when we've never seen
    // a bridge either.
    if (aisBridgeSeen.value) return 'via STANAG';
    if (aisStatus.value?.tcpUrl) return aisStatus.value.tcpUrl.replace(/^tcp:\/\//, '');
    return 'Not configured';
});

const latestStanagRate = computed(() => mqttStatus.value?.messagesPerMinute ?? null);

// AIS-bridge rate is sourced from the archive DB (MqttArchiveStatsService)
// rather than the in-process listener's timestamp queue.  Reason: the
// listener queue is only fed by rows that arrived via the broker
// connection this backend maintains, which under keep-alive cycling
// undercounts to near-zero when the wire path carries the bulk of
// traffic.  The DB counter runs a source-agnostic COUNT(*) filtered by
// `source_file LIKE '%ais_bridge%'`, so it sees rows from either
// writer.  Fall back to the listener counter for backends that pre-
// date the field so a stale UI still shows something.
const aisBridgeDbRate = computed<number | null>(() =>
    mqttStatus.value?.aisBridgeDedupedRowsPerMinute ??
    mqttStatus.value?.aisBridgeMessagesPerMinute ??
    null);
const latestAisRate = computed(() => {
    if (usingAisBridge.value) return aisBridgeDbRate.value;
    if (nativeAisLive.value)  return aisStatus.value?.messagesPerMinute ?? null;
    if (aisBridgeSeen.value)  return aisBridgeDbRate.value;
    return aisStatus.value?.messagesPerMinute ?? null;
});

// Stale annotation — appears when we've seen AIS-bridge traffic
// at least once this session (aisBridgeLastMessageAt != null) but
// the rate has dropped to zero.  Text like "silent 8m" surfaces
// an upstream outage before the operator has to eyeball missing
// vessels off the map.
const nowTick = ref(Date.now());
setInterval(() => { nowTick.value = Date.now(); }, 15000);
const aisSilentSuffix = computed<string | null>(() => {
    if (!usingAisBridge.value) return null;
    // Use the DB-sourced rate here too — otherwise a broker-side
    // listener stall trips "silent Nm" even while the wire path is
    // delivering plenty of bridge rows.
    const rate = aisBridgeDbRate.value ?? 0;
    if (rate > 0) return null;
    const lastIso = mqttStatus.value?.aisBridgeLastMessageAt as string | null | undefined;
    if (!lastIso) return null;
    const lastMs = Date.parse(lastIso);
    if (!Number.isFinite(lastMs)) return null;
    const ageMs = nowTick.value - lastMs;
    if (ageMs < 120_000) return null;   // grace period — brief lulls happen
    const mins = Math.floor(ageMs / 60_000);
    if (mins < 60) return `· silent ${mins}m`;
    const h = Math.floor(mins / 60), m = mins % 60;
    return m > 0 ? `· silent ${h}h ${m}m` : `· silent ${h}h`;
});
const aisTooltip = computed<string | undefined>(() => {
    if (!usingAisBridge.value) return undefined;
    const lastIso = mqttStatus.value?.aisBridgeLastMessageAt as string | null | undefined;
    if (!lastIso) return 'AIS bridge (via STANAG) — no messages seen yet this session';
    return `AIS bridge (via STANAG) — last message at ${new Date(lastIso).toISOString()}`;
});

const stanagHistory = ref<number[]>([]);
const aisHistory = ref<number[]>([]);
const wireHistory = ref<number[]>([]);

// Wire-transport rate — post-dedup rows tagged by the sidecar as
// coming through the puller-wire path (SourceFile starts with
// wire://).  Zero when the sidecar hasn't been redeployed with the
// tagging patch, or when no puller is connected.
const latestWireRate = computed<number | null>(() => mqttStatus.value?.wireMessagesPerMinute ?? null);
// "Wire is connected" = we've seen at least one row this window OR
// last window.  Cheap proxy for the sidecar's actual connection
// state which we don't currently expose over GraphQL.
const wireConnected = computed<boolean>(() =>
    (mqttStatus.value?.wireMessagesPerMinute ?? 0) > 0);

watch(latestStanagRate, (rate) => {
    if (rate == null) return;
    stanagHistory.value = [...stanagHistory.value.slice(-(HISTORY - 1)), rate];
});

watch(latestAisRate, (rate) => {
    if (rate == null) return;
    aisHistory.value = [...aisHistory.value.slice(-(HISTORY - 1)), rate];
});

watch(latestWireRate, (rate) => {
    if (rate == null) return;
    wireHistory.value = [...wireHistory.value.slice(-(HISTORY - 1)), rate];
});

function sparklinePoints(values: number[]): string | null {
    if (values.length < 2) return null;
    const max = Math.max(...values, 1);
    return values.map((v, i) => {
        const x = (i / (values.length - 1)) * SVG_W;
        const y = SVG_H - (v / max) * SVG_H * 0.85 - SVG_H * 0.05;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
}

const stanagPoints = computed(() => sparklinePoints(stanagHistory.value));
const aisPoints = computed(() => sparklinePoints(aisHistory.value));
const wirePoints = computed(() => sparklinePoints(wireHistory.value));

function formatRate(rate: number | null | undefined): string {
    if (rate == null) return '— msg/min';
    return `${Math.round(rate)} msg/min`;
}

function connectionDotStyle(running: boolean, connected: boolean): Record<string, string> {
    if (!running) return { background: 'rgba(255,255,255,0.2)' };
    if (connected) return { background: '#4ade80', boxShadow: '0 0 4px #4ade8088' };
    return { background: '#f59e0b', boxShadow: '0 0 4px #f59e0b88' };
}
</script>
